import { ChangeEvent, PropsWithChildren, useEffect } from 'react';

import {
  Accordion,
  Button,
  ChakraBox,
  ChakraFlex,
  ChakraText,
  Email,
  EquipUIFireflyV2Theme,
  EquipUITheme,
  PhoneNumber,
  TextInput,
  User,
} from '@equip.health/ui';
import { validate as validateEmail } from 'email-validator';

import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import util from '~/lib/util/util';

const { caption } = EquipUITheme.typography;
const { neutral } = EquipUIFireflyV2Theme.colors;

type ErrorState = Record<
  'toUserEmail' | 'toUserFirstName' | 'toUserLastName' | 'toUserPhone',
  boolean
>;

const defaultErrorState: ErrorState = {
  toUserEmail: false,
  toUserFirstName: false,
  toUserLastName: false,
  toUserPhone: false,
};

const TOP_LEVEL_ID = 'add-edit-survey-respondent';

const FormFieldContainer = ({
  children,
  label,
}: PropsWithChildren<{ label: string }>) => (
  <>
    <ChakraText {...caption} marginBottom="8px" marginLeft="40px">
      {label}
    </ChakraText>
    <ChakraFlex marginBottom="16px">{children}</ChakraFlex>
  </>
);

const IconContainer = ({ children }: PropsWithChildren) => (
  <ChakraBox
    alignItems="center"
    alignSelf="flex-start"
    background="white"
    display="flex"
    height="48px"
    marginRight="8px"
  >
    {children}
  </ChakraBox>
);

const FooterError = ({ children }: PropsWithChildren) => (
  <ChakraText
    {...caption}
    color="error"
    id={`${TOP_LEVEL_ID}__error`}
    marginRight="16px"
  >
    {children}
  </ChakraText>
);

type AddEditSurveyRespondentAccordionProps = {
  data: {
    errors: ErrorState;
    isCreateAssessmentError: boolean;
    isLoading: boolean;
    isSendEmailError: boolean;
    isUpdateRespondentError: boolean;
    onCancel: () => void;
    onConfirm: ({
      toUserEmail,
      toUserFirstName,
      toUserLastName,
      toUserPhone,
    }: SurveyRespondentProps) => void;
    toUserEmail: string;
    toUserFirstName: string;
    toUserLastName: string;
    toUserPhone: string;
  };
  onDismiss: () => void;
  isExpanded: boolean;
  setIsExpanded: (expanded: boolean) => void;
};

const AddEditSurveyRespondentAccordion = (
  props: AddEditSurveyRespondentAccordionProps,
) => {
  const { data, onDismiss, isExpanded, setIsExpanded } = props;
  const {
    errors,
    isCreateAssessmentError,
    isLoading,
    isSendEmailError,
    isUpdateRespondentError,
    onCancel,
    onConfirm,
    toUserEmail,
    toUserFirstName,
    toUserLastName,
    toUserPhone: toUserPhoneData,
  } = data ?? {};
  const toUserPhone =
    util.parsePhoneNumberWithDefaultCountry(toUserPhoneData) || '';
  const { addEditSurveyRespondentAccordion, setComponentVisibility } =
    useComponentPortalContext();
  const { isVisible } = addEditSurveyRespondentAccordion ?? {};

  const defaultComponentVisibilityDataParams = {
    ...props,
    errors: defaultErrorState,
    component: PortalComponent.ADD_EDIT_SURVEY_RESPONDENT_ACCORDION,
  };

  const onDataChange = (
    key: keyof AddEditSurveyRespondentAccordionProps['data'],
    value: string,
  ): void => {
    setComponentVisibility({
      ...defaultComponentVisibilityDataParams,
      data: { ...data, [key]: value },
      hasUnsavedChanges: true,
    });
  };

  const setToUserEmail = (e: ChangeEvent<HTMLInputElement>): void =>
    onDataChange('toUserEmail', e.target.value);

  const setToUserFirstName = (e: ChangeEvent<HTMLInputElement>): void =>
    onDataChange('toUserFirstName', e.target.value);

  const setToUserLastName = (e: ChangeEvent<HTMLInputElement>): void =>
    onDataChange('toUserLastName', e.target.value);

  const setToUserPhone = (value: string): void => {
    onDataChange('toUserPhone', value);
  };
  const trimmedEmail = (toUserEmail ?? '').trim();
  const trimmedFirstName = (toUserFirstName ?? '').trim();
  const trimmedLastName = (toUserLastName ?? '').trim();
  const isValidEmail = validateEmail(trimmedEmail);
  const isValidFirstName = !!trimmedFirstName.length;
  const isValidLastName = !!trimmedLastName.length;
  const isValidPhone = util.validatePhoneNumber(toUserPhone);

  useEffect(() => {
    if (isVisible) {
      setComponentVisibility({
        ...defaultComponentVisibilityDataParams,
        hasUnsavedChanges: false,
        data: {
          ...data,
          errors: defaultErrorState,
          toUserEmail: toUserEmail ?? '',
          toUserFirstName: toUserFirstName ?? '',
          toUserLastName: toUserLastName ?? '',
          toUserPhone,
        },
      });
    }
  }, [isVisible]);

  const handleConfirm = (): void => {
    setComponentVisibility({
      ...defaultComponentVisibilityDataParams,
      data: {
        ...data,
        errors: {
          toUserEmail: !isValidEmail,
          toUserFirstName: !isValidFirstName,
          toUserLastName: !isValidLastName,
          toUserPhone: toUserPhone && !isValidPhone,
        },
      },
    });

    if (
      [isValidEmail, isValidFirstName, isValidLastName, isValidPhone].every(
        (isValid: boolean): boolean => isValid,
      )
    ) {
      onConfirm({
        toUserEmail: trimmedEmail,
        toUserFirstName: trimmedFirstName,
        toUserLastName: trimmedLastName,
        toUserPhone,
      });
    }
  };

  const handleDismissMenu = (): void => {
    onCancel();
    onDismiss();
  };

  return (
    <Accordion
      dismissMenu={handleDismissMenu}
      footer={
        <ChakraFlex align="center" justify="flex-end" width="100%">
          {isCreateAssessmentError && (
            <FooterError>Error initializing survey</FooterError>
          )}
          {isSendEmailError && <FooterError>Error sending email</FooterError>}
          {isUpdateRespondentError && (
            <FooterError>Error saving user details</FooterError>
          )}
          <Button
            id={`${TOP_LEVEL_ID}__confirm`}
            isDisabled={
              !toUserFirstName?.trim() ||
              !toUserLastName?.trim() ||
              !toUserEmail?.trim() ||
              !toUserPhone ||
              !isValidPhone ||
              !isValidEmail ||
              isLoading
            }
            isLoading={isLoading}
            onClick={handleConfirm}
          >
            Confirm
          </Button>
        </ChakraFlex>
      }
      id={TOP_LEVEL_ID}
      isExpanded={isExpanded}
      panelStyling={{ height: 'min-content' }}
      setIsExpanded={setIsExpanded}
      title="Survey respondent details"
    >
      <ChakraBox padding="24px">
        <FormFieldContainer label="The preferred first and last name of the patient or caregiver that will appear on survey notifications">
          <IconContainer>
            <User fill={neutral.secondary} width="20px" />
          </IconContainer>
          <TextInput
            errorMessage="First name cannot be blank"
            id={`${TOP_LEVEL_ID}__first-name`}
            isInvalid={!!errors?.toUserFirstName}
            margin="0 0 0 8px"
            onChange={setToUserFirstName}
            placeholder="Enter first name"
            value={toUserFirstName ?? ''}
          />
          <TextInput
            errorMessage="Last name cannot be blank"
            id={`${TOP_LEVEL_ID}__last-name`}
            isInvalid={!!errors?.toUserLastName}
            margin="0 0 0 8px"
            onChange={setToUserLastName}
            placeholder="Enter last name"
            value={toUserLastName ?? ''}
          />
        </FormFieldContainer>
        <FormFieldContainer label="Reminders and updates about the survey will be sent to this email address">
          <IconContainer>
            <Email fill={neutral.secondary} width="20px" />
          </IconContainer>
          <TextInput
            errorMessage="Not a valid email"
            id={`${TOP_LEVEL_ID}__email`}
            isInvalid={!isValidEmail}
            margin="0 0 0 8px"
            onChange={setToUserEmail}
            placeholder="Enter email"
            value={toUserEmail ?? ''}
          />
        </FormFieldContainer>
        <FormFieldContainer label="Access codes will be sent to this number when the patient or caregiver tries to access the survey">
          <PhoneNumber
            formMarginBottom="8px"
            id={`${TOP_LEVEL_ID}__phone-number`}
            isInvalid={toUserPhone && !isValidPhone}
            onChange={(value) => {
              setToUserPhone(value);
            }}
            paddingInlineStart="28px"
            value={toUserPhone || ''}
          />
        </FormFieldContainer>
      </ChakraBox>
    </Accordion>
  );
};

export default AddEditSurveyRespondentAccordion;
